import axios, { AxiosInstance, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import _ from 'lodash';
import GlobalNotification from '../component/common/snackbar';
import i18n from '../i18n';
import { LOCAL_STORAGE_KEYS } from '../modules/cache/local-storage.constant';
import LSM from '../modules/cache/tool-local-storage';
import qs from 'qs';
import { checkToken, cleanAccountCache } from '../constants/tool.check-token';

//  $ 基础url (三种不同的环境：dev、stable、release)
const URL = process.env.REACT_APP_BASE_URL ?? 'https://dambuster-dev-dynamic-api.evophotic.com';
// 本地
// const URL = 'http://192.168.3.115:9100';

const VERSION = 'v1';
const VERSION2 = 'v2';
const VERSION2_5 = 'v2.5';
const VERSION3 = 'v3';
const VERSION3_0 = 'v3.0';

/** v1 API 根路径（如 `GET /v1/public/restaurant`） */
export const BASE_URL = `${URL}/${VERSION}`;
export const BASE_URL_2 = `${URL}/${VERSION2}`;
export const BASE_URL_2_5 = `${URL}/${VERSION2_5}`;
export const BASE_URL_3 = `${URL}/${VERSION3}`;
export const BASE_URL_3_0 = `${URL}/${VERSION3_0}`;

// $ 创建axios对象
export const instant: AxiosInstance = axios.create({
    baseURL: BASE_URL,
    // timeout: 20000,
    headers: { 'authorization-type': 'BASIC_AUTH' }
})

/**
 * 判断是否为「用户名密码换 token」的登录请求；此类请求不得附带本地缓存的 Bearer，避免服务端按旧 token 处理。
 * @param config - Axios 请求配置（合并 baseURL + url 后匹配路径）
 */
const isAuthPasswordLoginRequest = (config: InternalAxiosRequestConfig<{ version: string }>): boolean => {
    const fullPath = `${config.baseURL ?? ''}${config.url ?? ''}`;
    return /\/auth\/(staff|admin|reseller)\/token\b/.test(fullPath);
};

//  请求处理
const requestConfig = (config: InternalAxiosRequestConfig<{ version: string }>) => {
    // 原来的配置
    config = config || {};
    // 获取token
    const token = LSM.get(LOCAL_STORAGE_KEYS.TOKEN) || {};
    // 检查token是否失效
    if (!isGuestPage()) {
        checkToken(token)
    }
    if (config.method === 'get') {
        // 处理经销商请求参数
        const belongedReseller = config.params?.belonged_reseller;
        if (belongedReseller?.includes('Admin')) {
            config.params = {
                ...config.params,
                belonged_reseller: belongedReseller.filter(item => item !== 'Admin'),
                filter_admin: true
            };
        }
        // 如果是get请求，且params是数组类型如arr=[1,2]，则转换成arr=1&arr=2
        config.paramsSerializer = {
            serialize: (params) => qs.stringify(params, { arrayFormat: 'repeat' })
        };
    }
    // 配置请求头的 auth：登录换 token 接口不传 Bearer
    if (isAuthPasswordLoginRequest(config)) {
        delete config.headers.Authorization;
    } else {
        config.headers['Authorization'] = `Bearer ${_.get(token, 'accessToken') ?? ''}`;
    }
    config.headers['Access-Control-Allow-Origin'] = '*';
    return {
        ...config
        // 这里可以加一些公共的配置参数
    }
}

// 请求出错
const requestError = (error: any) => {
    return Promise.reject(error);
}

// 统一响应请求
const responseConfig = (config: AxiosResponse): AxiosResponse => {
    return config.data;
}

/**
 * 判断请求是否因 AbortController / axios 取消而结束；此类情况不应走「接口失败」分支与业务 Toast。
 */
export function isHttpRequestCanceled(error: unknown): boolean {
    if (axios.isCancel(error as any)) {
        return true;
    }
    const e = error as { code?: string; name?: string };
    if (e?.code === 'ERR_CANCELED') {
        return true;
    }
    if (e?.name === 'CanceledError' || e?.name === 'AbortError') {
        return true;
    }
    return false;
}

// 响应出错
const responseError = (error: { message?: string; }) => {
    // 用户离开页面等触发的取消：不弹全局 5xx/超时等提示，与业务层约定 `message: 'canceled'`
    if (isHttpRequestCanceled(error)) {
        return {
            error: true,
            status: _.get(error, 'response.status'),
            result: undefined,
            message: 'canceled',
        };
    }
    // 错误提示
    const message = `${_.get(error, 'response.data.error.message', '')}`;
    const details = `${_.get(error, 'response.data.error.details', '')}`;
    // $ 请求超时
    if (error?.message?.search(/timeout/) !== -1) {
        GlobalNotification.error(`${i18n.t('http_message_network_request_timed_out')}`);
    }
    //$ 当服务器发生错误时，直接弹出窗口
    if (`${_.get(error, 'response.status')}`.startsWith('5')) {
        console.error(error);
        GlobalNotification.error(i18n.t('request_failure_text_service_is_not_available'))
    }
    //$ token失效,token过期

    if (message.includes('please log in again') || details.includes('invalid token')) {
        if (!isGuestPage()) {
            GlobalNotification.error(i18n.t('general_text_Not_logged_in_for_a_long_time_need_to_log_in_again'))
            cleanAccountCache(true)
        }
    }
    // 请求出错
    return {
        error: true,
        status: _.get(error, 'response.status'),
        result: _.get(error, 'response.data.error') || _.get(error, 'response.data'),
        message: error.message
    };
}

// 添加拦截器
instant.interceptors.request.use(requestConfig, requestError);
instant.interceptors.response.use(responseConfig, responseError);

// 直接返回接口报错信息
export const httpError = (error: { response: { data: unknown; status: number; statusText: string; }; }) => {
    return {
        data: error?.response?.data,
        status: error?.response?.status,
        statusText: error?.response?.statusText
    }
}

// $ 这个类型再考虑  (这个T有可能是数组 类型在考虑)
export type HttpSuccessOrErrorType<T> = { data: T } extends WhenError ? WhenError : T & WhenError;
export type WhenError = { error: true, message: string, result: HttpErrorType }

export type HttpErrorType = {
    code?: number;
    details?: Record<string, unknown> | string;
    message?: string;
    statusCode?: number;
}

/** MongoDB 写操作返回的时间戳信息。 */
export interface MongoOperationTime {
    /** MongoDB 操作时间戳。 */
    ts: string;
    /** MongoDB 选举任期。 */
    t: number;
}

/** MongoDB 集群签名信息。 */
export interface MongoClusterTimeSignature {
    /** 集群时间签名哈希。 */
    hash: string;
    /** 生成签名的密钥 ID。 */
    keyId: string;
}

/** MongoDB 集群时间信息。 */
export interface MongoClusterTime {
    /** 集群逻辑时间。 */
    clusterTime: string;
    /** 集群时间签名。 */
    signature: MongoClusterTimeSignature;
}

/** MongoDB 写操作使用的连接信息。 */
export interface MongoConnection {
    /** 数据库连接 ID。 */
    id: number;
    /** 数据库主机地址。 */
    host: string;
    /** 数据库端口。 */
    port: number;
}

/** MongoDB 更新操作结果，可供普通写接口复用。 */
export interface MongoWriteResult {
    /** MongoDB 原始更新结果。 */
    result: {
        /** 匹配到的记录数。 */
        n: number;
        /** 实际修改的记录数。 */
        nModified: number;
        /** MongoDB 操作时间。 */
        opTime: MongoOperationTime;
        /** 当前主节点选举 ID。 */
        electionId: string;
        /** MongoDB 操作是否成功，1 表示成功。 */
        ok: number;
        /** 集群时间信息。 */
        $clusterTime: MongoClusterTime;
        /** 操作时间字符串。 */
        operationTime: string;
    };
    /** 执行写操作的数据库连接。 */
    connection: MongoConnection;
    /** 实际修改的记录数。 */
    modifiedCount: number;
    /** 插入后生成的 ID，无 upsert 时为 null。 */
    upsertedId: string | number | null;
    /** upsert 插入的记录数。 */
    upsertedCount: number;
    /** 匹配到的记录数。 */
    matchedCount: number;
    /** MongoDB 兼容字段：匹配到的记录数。 */
    n: number;
    /** MongoDB 兼容字段：实际修改的记录数。 */
    nModified: number;
    /** MongoDB 操作时间。 */
    opTime: MongoOperationTime;
    /** 当前主节点选举 ID。 */
    electionId: string;
    /** 集群时间信息。 */
    $clusterTime: MongoClusterTime;
    /** 操作时间字符串。 */
    operationTime: string;
}

// 表格返回值类型
export type ListResults<T> = { dbResults: T[], totalCount: Array<{ count: number }> }
// 表格请求参数类型
export type ListGetRequestParams<T> = {
    // 默认1
    pagination_page: number;
    // 默认100
    pagination_per_page: number;
    // 默认isv_connect_create(加入时间)
    sort_key?: string;
    // 默认-1
    sort_value?: number;
} & T

// export type HttpSuccessType<T = unknown> = {
//     type: 'SUCCESS';
//     data: T;
//     status: number;
//     statusText: string;
// }

// export type HttpErrorType = {
//     type: 'ERROR';
//     data: HttpErrorType;
//     status: number;
//     statusText: string;
// }


export default instant;


export declare type OkPacket = {
    fieldCount: number;
    affectedRows: number;
    insertId: number;
    serverStatus: number;
    warningCount: number;
    message: string;
    protocol41: boolean;
    changedRows: number;
}

export type ResponseErrorType = {
    error: boolean;
    status: any;
    result: any;
}
export interface WillStopConfig extends InternalAxiosRequestConfig<{ version: string }> {
    stop_by_unmount?: boolean;
}

export function createPageReqDocker(this) {
    if (!this.movableInterceptonr) {
        this.ctrl = new AbortController();
        this.movableInterceptonr = instant.interceptors.request.use((config: WillStopConfig) => {
            if (config.stop_by_unmount) {
                return { signal: this.ctrl.signal, ...config }
            } else {
                return config
            }
        })
    }
}
export function removeReq(this) {
    if (this.movableInterceptonr) {
        this.ctrl.abort();
        instant.interceptors.request.eject(this.movableInterceptonr);
        this.movableInterceptonr = null
    }
}

// 是否是游客页面
export function isGuestPage() {
    const hash = window.location.hash;
    return hash.includes('upload-fiskaly');
}

